import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import '../../assets/scss/account.scss';

import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import Layout from '../../components/Layout';
import Toparea from '../../components/Toparea';
import { finishLogin, startKakaoLogin, startGoogleLogin, platform, saveLogin, startGuest } from '../../utils/native';

const Login = (props) => {
	const navigate = useNavigate();
	// state.save: a guest came to keep their progress (pick a sign-up method); state.email: open the email form
	const { state } = useLocation();
	const save = !!state?.save;
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [errorMsg, setErrorMsg] = useState('');
	// Start screen shows the three big buttons; the email form opens from the text link
	const [emailForm, setEmailForm] = useState(!!state?.email);
	const [providers, setProviders] = useState({}); // social buttons the server has keys for

	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (token) {
			navigate('/Main');
			return;
		}

		// Back from the Kakao login on the web: /Login?code=... or ?error=...
		const q = new URLSearchParams(window.location.search);
		if (q.get('code')) finishLogin(q.get('code')).catch(() => setErrorMsg('로그인 시간이 지났어요. 다시 시도해 주세요.'));
		else if (q.get('error') && q.get('error') !== 'cancelled') setErrorMsg('로그인에 실패했습니다. 다시 시도해 주세요.');

		axios.get('/api/auth/providers').then(res => setProviders(res.data)).catch(() => { });
	}, [navigate]);


	const handleLogin = async () => {
		if (!email) {
			setErrorMsg('이메일을 입력해주세요.');
			return;
		}
		if (!password) {
			setErrorMsg('비밀번호를 입력해주세요.');
			return;
		}

		try {
			const response = await axios.post('/api/auth/login', { email, password });
			const { token, user } = response.data;

			await saveLogin(token, user.email);
			navigate('/Main');
		} catch (err) {
			console.error(err);
			if (err.response && err.response.status === 401) {
				setErrorMsg('이메일 또는 비밀번호가 일치하지 않습니다.');
			} else {
				setErrorMsg(err.response?.data?.error || '로그인 중 오류가 발생했습니다.');
			}
		}
	};

	// Sign in with Apple (iOS app only; App Store requires it next to other social logins)
	const handleAppleLogin = async () => {
		try {
			const { response } = await SignInWithApple.authorize({ clientId: 'com.dubuck.app', redirectURI: 'https://dubuck.com', scopes: 'email name' });
			const name = [response.givenName, response.familyName].filter(Boolean).join(' ');
			const { data } = await axios.post('/api/auth/apple', { identityToken: response.identityToken, authorizationCode: response.authorizationCode, name });
			await saveLogin(data.token, data.user.email);
			navigate('/Main');
		} catch (err) {
			// code 1001 = the user closed the Apple sheet
			if (!String(err.message || err).includes('1001')) setErrorMsg('Apple 로그인에 실패했습니다.');
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') handleLogin();
	};

	const handleGuest = async () => {
		try {
			await startGuest();
			navigate('/Main');
		} catch (err) {
			setErrorMsg('체험을 시작하지 못했어요. 잠시 후 다시 시도해 주세요.');
		}
	};

	return (
		<Layout containName='main'>
			<Toparea />
			<div className="login-group">
				{emailForm ? (
					<>
						<div className="input-wrap">
							<label htmlFor="email">이메일</label>
							<input type="text" inputMode="email" id="email" autoComplete="email" value={email} onChange={e => { setEmail(e.target.value); setErrorMsg(''); }} onKeyDown={handleKeyDown} />
						</div>
						<div className="input-wrap">
							<label htmlFor="pw">비밀번호</label>
							<input type="password" id="pw" autoComplete="current-password" value={password} onChange={e => { setPassword(e.target.value); setErrorMsg(''); }} onKeyDown={handleKeyDown} />
						</div>
						{errorMsg && <p className="login-error" role="alert">{errorMsg}</p>}
						<button type="button" className="btn auth-btn" onClick={handleLogin}>로그인</button>
						<div className="login-links">
							<Link to="/Find">비밀번호 찾기</Link>
							<Link to="/Join">회원가입</Link>
						</div>
						<button type="button" className="login-switch" onClick={() => { setEmailForm(false); setErrorMsg(''); }}>다른 방법으로 시작하기</button>
					</>
				) : (
					<>
						{save ? (
							<p className="login-title">기록을 저장할 방법을 골라 주세요<small>어떤 방법이든 체험 기록이 그대로 이어져요</small></p>
						) : (
							<>
								<button type="button" className="btn auth-btn" onClick={handleGuest}>지금 시작하기</button>
								<p className="login-hint">가입 없이 바로 체험하고, 기록은 나중에 가입하면 이어져요</p>
							</>
						)}
						<div className="sns">
							<p className="sns-title">SNS로 계속하기</p>
							<div className="sns-row">
								<button type="button" className="sns-btn" onClick={startKakaoLogin} aria-label="카카오로 계속하기">
									<span className="sns-icon is-kakao">
										<svg width="24" height="24" viewBox="0 0 24 24" fill="#191919" aria-hidden="true">
										<path d="M12 3c-4.97 0-9 3.185-9 7.115 0 2.558 1.707 4.8 4.27 6.054l-.841 3.097c-.021.082.025.165.101.193.028.01.059.011.088.003L10.126 16.9c.61.084 1.238.13 1.874.13 4.97 0 9-3.185 9-7.115S16.97 3 12 3z" />
										</svg>
									</span>
									카카오
								</button>
								{providers.google && (
									<button type="button" className="sns-btn" onClick={startGoogleLogin} aria-label="Google로 계속하기">
										<span className="sns-icon is-google">
											<svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
											<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
											<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
											<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
											<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
											</svg>
										</span>
										구글
									</button>
								)}
								{platform === 'ios' && (
									<button type="button" className="sns-btn" onClick={handleAppleLogin} aria-label="Apple로 계속하기">
										<span className="sns-icon is-apple">
											<svg width="20" height="22" viewBox="0 0 384 512" fill="#fff" aria-hidden="true">
											<path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
											</svg>
										</span>
										애플
									</button>
								)}
							</div>
						</div>
						{errorMsg && <p className="login-error" role="alert">{errorMsg}</p>}
						{save && <Link to="/Join" className="auth-btn is-line">이메일로 가입하기</Link>}
						<button type="button" className="login-switch" onClick={() => { setEmailForm(true); setErrorMsg(''); }}>{save ? '이미 계정이 있어요 · 이메일 로그인' : '이메일로 로그인 · 회원가입'}</button>
						<p className="login-legal">
							계속하면 <Link to="/Terms">이용약관</Link>과 <Link to="/Privacy">개인정보처리방침</Link>에 동의하게 됩니다.
						</p>
					</>
				)}
				<p className="login-copyright">© 2023 - 2026 codemodo & kim-joy & pmineq</p>
			</div>
		</Layout>
	);
};

export default Login;